import { z } from 'zod';

export const RegistrationStatusEnum = z.enum([
  'APPROVED',
  'PENDING_PREREQUISITE_REVIEW',
  'DENIED',
  'HOLD',
  'PAID',
]);

export type RegistrationStatus = z.infer<typeof RegistrationStatusEnum>;

export const Registration = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  profileId: z.string(),
  eventId: z.string(),
  routeId: z.string().nullable(),
  categoryId: z.string(),
  status: RegistrationStatusEnum,
  holdExpiresAt: z.coerce.date().nullable().optional(),
  stripePaymentIntentId: z.string().nullable().optional(),
  amountPaidCents: z.number().nullable().optional(),
  currency: z.string().nullable().optional(),
});

export type Registration = z.infer<typeof Registration>;

export const RegisterForEventInput = z.object({
  eventId: z.string().uuid(),
  routeId: z.string().uuid().nullable(),
  categoryId: z.string().uuid(),
  offPlatformClaim: z.boolean().optional(),
});

export type RegisterForEventInput = z.infer<typeof RegisterForEventInput>;

export const PrerequisiteCheckResult = z.object({
  prerequisiteEvent: z.object({
    id: z.string(),
    name: z.string(),
  }),
  satisfied: z.boolean(),
});

export type PrerequisiteCheckResult = z.infer<typeof PrerequisiteCheckResult>;

export const CheckPrerequisitesResult = z.object({
  allSatisfied: z.boolean(),
  prerequisites: z.array(PrerequisiteCheckResult),
});

export type CheckPrerequisitesResult = z.infer<typeof CheckPrerequisitesResult>;

export const PrerequisiteStatusItem = z.object({
  id: z.string(),
  name: z.string(),
  satisfied: z.boolean().nullable(),
});

export type PrerequisiteStatusItem = z.infer<typeof PrerequisiteStatusItem>;

export const PrerequisiteStatus = z.object({
  prerequisites: z.array(PrerequisiteStatusItem),
  allSatisfied: z.boolean().nullable(),
});

export type PrerequisiteStatus = z.infer<typeof PrerequisiteStatus>;

export const PendingApprovalItem = z.object({
  id: z.string(),
  claimedAt: z.coerce.date(),
  status: RegistrationStatusEnum,
  profile: z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    email: z.string(),
  }),
  /** Prerequisites defined for the event — shown to organizer as what the rider needs verified. */
  prerequisites: z.array(z.object({ id: z.string(), name: z.string() })),
});

export type PendingApprovalItem = z.infer<typeof PendingApprovalItem>;

export const GetPendingApprovalsResult = z.object({
  pending: z.array(PendingApprovalItem),
  denied: z.array(PendingApprovalItem),
});

export type GetPendingApprovalsResult = z.infer<
  typeof GetPendingApprovalsResult
>;

export const ReviewRegistrationInput = z.object({
  registrationId: z.string().uuid(),
  eventId: z.string().uuid(),
});

export type ReviewRegistrationInput = z.infer<typeof ReviewRegistrationInput>;
